import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import type { WalletCurrency } from 'store/models/walletCurrency';

import { PaymentType } from '../Modulor/CurrencySelectContainer/types';
import type { CurrencyBase } from '../store/slices/wallet';
import { getIconUrl } from './pwContext';

const CRYPTO_ORDER = ['USDT', 'USDC', 'ETH', 'BTC', 'LTC', 'BCH'];

export const getCurrencyTab = (currency?: CurrencyBase | null): PaymentType => {
  if (currency?.isFiat) return PaymentType.FIAT;

  return PaymentType.CRYPTO;
};

export const sortCryptoCurrencies = (currencies: CurrencyBase[]) =>
  [...currencies].sort((a, b) => {
    const indexA = CRYPTO_ORDER.indexOf(a.alpha3);
    const indexB = CRYPTO_ORDER.indexOf(b.alpha3);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.alpha3.localeCompare(b.alpha3);
  });

export const getSelectedCurrency = ({
  walletCurrencies,
  userCurrency,
  groups,
  initCurrency,
}: {
  walletCurrencies: WalletCurrency[];
  userCurrency: string | null;
  groups?: UIPaymentGroups[] | UIPayoutGroups[];
  initCurrency?: string;
}): CurrencyBase | null => {
  const hasWallets = walletCurrencies.length > 0;
  const hasGroups = groups && groups.length > 0;

  // 1. If we have wallets, they are our top priority
  if (hasWallets) {
    const targetAlpha3 = initCurrency || userCurrency;

    // Try to find specifically requested wallet, otherwise take the first one
    const wallet = (targetAlpha3 && walletCurrencies.find((c) => c.alpha3 === targetAlpha3)) || walletCurrencies[0];

    return {
      id: wallet.currency_id,
      alpha3: wallet.alpha3,
      isFiat: wallet.is_fiat,
      isOnRamp: wallet.is_on_ramp,
      iconUrl: getIconUrl(wallet.icon_url),
      // Precision usually comes from groups metadata
      currencyPrecision: hasGroups ? groups[0].currencyPrecision : undefined,
    };
  }

  // 2. Fallback: If no wallets at all, build currency from the first group
  if (hasGroups) {
    const firstGroup = groups[0];
    return {
      id: firstGroup.currencyId,
      alpha3: firstGroup.currencyAlpha3 ?? '',
      currencyPrecision: firstGroup.currencyPrecision,
      isFiat: true,
      isOnRamp: false,
    };
  }

  return null;
};

export const mapToCurrencyBase = (currency: WalletCurrency): CurrencyBase => ({
  id: currency.currency_id,
  alpha3: currency.alpha3,
  isFiat: currency.is_fiat,
  isOnRamp: currency.is_on_ramp,
  iconUrl: getIconUrl(currency.icon_url),
});

export const getWalletInitData = (
  walletCurrencies: CurrencyBase[],
  selectedCurrency: CurrencyBase | null,
  isDepositFlow: boolean,
) => {
  const { fiatCurrencies, initialCryptoCurrencies, initialOnRampCurrencies } = walletCurrencies.reduce(
    (acc, currency) => {
      if (currency.isFiat) {
        acc.fiatCurrencies.push(currency);
      } else {
        acc.initialCryptoCurrencies.push(currency);
      }

      if (currency.isOnRamp && isDepositFlow) {
        acc.initialOnRampCurrencies.push(currency);
      }

      return acc;
    },
    {
      fiatCurrencies: [] as CurrencyBase[],
      initialCryptoCurrencies: [] as CurrencyBase[],
      initialOnRampCurrencies: [] as CurrencyBase[],
    },
  );
  const cryptoCurrencies = sortCryptoCurrencies(initialCryptoCurrencies);
  const onRampCurrencies = sortCryptoCurrencies(initialOnRampCurrencies);

  const tabs: PaymentType[] = [];
  const hasFiat = fiatCurrencies.length > 0;
  const hasCrypto = cryptoCurrencies.length > 0;
  const hasOnRamp = onRampCurrencies.length > 0;

  if (hasCrypto) tabs.push(PaymentType.CRYPTO);
  if (hasFiat) tabs.push(PaymentType.FIAT);
  if (hasOnRamp) tabs.push(PaymentType.ON_RAMP);

  const finalTabs = tabs.length >= 2 ? tabs : [];

  const activeTab = getCurrencyTab(selectedCurrency);

  return {
    tabs: finalTabs,
    activeTab,
    fiatCurrencies,
    cryptoCurrencies,
    onRampCurrencies,
  };
};
